import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { opsApi } from "@/features/operations/api";
import { roleLabel } from "@/features/auth/roles";

type FormState = {
    role: string;
    full_name: string;
    email: string;
    phone: string;
    password: string;
    store_id: string;
    vehicle_type: string;
    vehicle_number: string;
    salary_rate_per_hour: number;
};

const empty: FormState = {
    role: "staff",
    full_name: "",
    email: "",
    phone: "",
    password: "",
    store_id: "",
    vehicle_type: "bike",
    vehicle_number: "",
    salary_rate_per_hour: 100,
};

export default function AdminTeam() {
    const qc = useQueryClient();
    const [filter, setFilter] = useState<string>("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(empty);

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "team", filter],
        queryFn: () => opsApi.listTeam(filter ? { role: filter } : undefined),
    });
    const stores = useQuery({
        queryKey: ["admin", "stores-lookup"],
        queryFn: () => opsApi.listStores(),
    });

    const createMut = useMutation({
        mutationFn: (p: typeof empty) => opsApi.createTeamMember(p),
        onSuccess: () => {
            toast.success("Team member added");
            qc.invalidateQueries({ queryKey: ["admin", "team"] });
            setDialogOpen(false);
            setForm(empty);
        },
        onError: (e: unknown) => {
            toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed to add");
        },
    });

    const blockMut = useMutation({
        mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
            opsApi.updateTeamMember(id, { is_blocked: blocked }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "team"] }),
    });

    const roleOptions = ["operations_admin", "store_admin", "staff", "rider"];

    return (
        <div data-testid="admin-team-page" className="space-y-6">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold">Team</h1>
                    <p className="text-sm text-muted-foreground">
                        Operations admins, store admins, staff and riders.
                    </p>
                </div>
                <Button onClick={() => { setForm(empty); setDialogOpen(true); }} className="bg-brand text-white" data-testid="admin-team-new">
                    <Plus className="mr-2 h-4 w-4" /> Add team member
                </Button>
            </header>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => setFilter("")}
                    data-testid="admin-team-filter-all"
                    className={`rounded-full px-3 py-1 text-xs font-bold ${filter === "" ? "bg-ink text-white" : "bg-white text-ink"}`}
                >
                    All
                </button>
                {roleOptions.map((r) => (
                    <button
                        key={r}
                        onClick={() => setFilter(r)}
                        data-testid={`admin-team-filter-${r}`}
                        className={`rounded-full px-3 py-1 text-xs font-bold ${filter === r ? "bg-ink text-white" : "bg-white text-ink"}`}
                    >
                        {roleLabel(r)}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <p>Loading…</p>
            ) : (
                <div className="overflow-x-auto rounded-2xl bg-white">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Store</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.items.map((m) => {
                                const store = stores.data?.items.find((s) => s.id === m.store_id);
                                return (
                                    <tr key={m.id} className="border-t border-border/40" data-testid={`admin-team-row-${m.email}`}>
                                        <td className="px-4 py-3 font-semibold">{m.full_name}</td>
                                        <td className="px-4 py-3">{m.email}</td>
                                        <td className="px-4 py-3">{roleLabel(m.role)}</td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{store?.name ?? "—"}</td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${m.is_blocked ? "bg-error/10 text-error" : "bg-brand/10 text-brand"}`}>
                                                {m.is_blocked ? "Blocked" : "Active"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => blockMut.mutate({ id: m.id, blocked: !m.is_blocked })}
                                                data-testid={`admin-team-toggle-${m.email}`}
                                            >
                                                {m.is_blocked ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!data?.items.length && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                                        No team members yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="admin-team-dialog">
                    <DialogHeader>
                        <DialogTitle>Add team member</DialogTitle>
                    </DialogHeader>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const payload: typeof empty = { ...form };
                            if (!["store_admin", "staff", "rider"].includes(form.role)) {
                                payload.store_id = "";
                            }
                            createMut.mutate(payload);
                        }}
                        className="space-y-3"
                    >
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="t-role">Role</Label>
                                <select
                                    id="t-role"
                                    data-testid="admin-team-role"
                                    className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                >
                                    {roleOptions.map((r) => (
                                        <option key={r} value={r}>{roleLabel(r)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="t-store">Store</Label>
                                <select
                                    id="t-store"
                                    data-testid="admin-team-store"
                                    className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
                                    value={form.store_id}
                                    onChange={(e) => setForm({ ...form, store_id: e.target.value })}
                                    disabled={form.role === "operations_admin"}
                                >
                                    <option value="">— None —</option>
                                    {stores.data?.items.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="t-name">Full name</Label>
                            <Input id="t-name" data-testid="admin-team-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="t-email">Email</Label>
                                <Input id="t-email" type="email" data-testid="admin-team-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                            </div>
                            <div>
                                <Label htmlFor="t-phone">Phone</Label>
                                <Input id="t-phone" data-testid="admin-team-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="t-pass">Password</Label>
                                <Input id="t-pass" type="password" data-testid="admin-team-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
                            </div>
                            <div>
                                <Label htmlFor="t-rate">Salary/hr (₹)</Label>
                                <Input id="t-rate" type="number" min="0" value={form.salary_rate_per_hour} onChange={(e) => setForm({ ...form, salary_rate_per_hour: Number(e.target.value) })} />
                            </div>
                        </div>
                        {form.role === "rider" && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="t-vt">Vehicle type</Label>
                                    <select id="t-vt" className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}>
                                        {["bike", "scooter", "bicycle", "car"].map((v) => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="t-vn">Vehicle #</Label>
                                    <Input id="t-vn" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="submit" data-testid="admin-team-save" className="bg-brand text-white" disabled={createMut.isPending}>
                                Create account
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
